import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FileText,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  Loader2,
  Check,
  X,
  Search,
  ExternalLink,
  Send,
  AlertCircle,
  Mail,
} from "lucide-react";
import { fmtUK } from "../lib/dateUtils";
import MatterSearchUnavailableNotice from "../components/MatterSearchUnavailableNotice";
// Aliased: OutgoingPostDetail already has a local searchMatters callback, and an
// import that shadows or is shadowed by a local name is how you get silent
// recursion. The alias makes the call site unambiguous in every file.
import { searchMatters as searchMattersApi, MatterSearchUnavailable, MATTER_SEARCH_UNAVAILABLE_MESSAGE } from "../lib/matterSearch";
import { toast } from "sonner";
import BonsaiMate from "../components/BonsaiMate";
import { RecordTimeField, EMPTY_TIME, recordTouchpointTime, timeFieldProblem } from "../components/time/RecordTimeField";

const OUTGOING_DOC_TYPES = [
  { value: "letter", label: "Letter" },
  { value: "report", label: "Report" },
  { value: "contract", label: "Contract" },
  { value: "deed", label: "Deed" },
  { value: "completion_statement", label: "Completion Statement" },
  { value: "other", label: "Other" },
];

function DocumentPreview({ itemId, filename, sharepointId }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const loadPreview = async () => {
    if (!sharepointId || sharepointId.startsWith("manual-")) {
      setPreviewError("Document was created manually — no SharePoint file to preview");
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await axios.get(`${API}/postintake/outgoing/${itemId}/preview`);
      setPreviewUrl(res.data.preview_url);
      setPreviewType(res.data.type);
    } catch (err) {
      setPreviewError(errorMessage(err, "Could not load preview"));
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, [itemId, sharepointId]);

  if (previewUrl) {
    if (previewType === "web") {
      return (
        <div className="bg-slate-100 rounded-md border border-navy-border p-4" data-testid="pdf-preview">
          <p className="text-sm text-txt-muted mb-2">{filename}</p>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-teal/90">
            <ExternalLink className="w-4 h-4" /> Open in SharePoint
          </a>
        </div>
      );
    }
    return (
      <div className="bg-slate-100 rounded-md overflow-hidden border border-navy-border" data-testid="pdf-preview">
        <iframe
          src={previewUrl}
          title={filename || "Document Preview"}
          className="w-full h-[600px]"
          style={{ border: "none", overflow: "auto" }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
        <div className="p-2 border-t border-navy-border flex justify-end">
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
            className="text-xs text-teal hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Open in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 rounded-md h-64 flex items-center justify-center border border-navy-border" data-testid="pdf-preview-placeholder">
      <div className="text-center text-slate-400 px-4">
        {previewLoading ? (
          <><Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" /><p className="text-sm">Loading preview...</p></>
        ) : (
          <>
            <FileText className="w-10 h-10 mx-auto mb-2" />
            <p className="font-medium text-sm">{filename || "Document"}</p>
            {previewError && <p className="text-xs mt-2 text-status-amber">{previewError}</p>}
            {!previewError?.includes("manually") && (
              <button onClick={loadPreview} className="mt-2 text-xs text-teal hover:underline">Retry</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function OutgoingPostDetail() {
  // BM-H4112: the route is /outgoing-post/:postId (App.js). Reading
  // { itemId } got undefined, so every item fetched /outgoing/undefined and
  // the page never loaded its record. Found by the BM-H4112 Playwright check.
  const { postId: itemId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  // Form state
  const [selectedMatter, setSelectedMatter] = useState("");
  const [selectedDocType, setSelectedDocType] = useState("");
  const [toParty, setToParty] = useState("");
  const [notes, setNotes] = useState("");
  const [dateSent, setDateSent] = useState(new Date().toISOString().slice(0, 10));
  const [rejectReason, setRejectReason] = useState("");
  const [timeBox, setTimeBox] = useState(EMPTY_TIME); // BM-H4112

  // Auto-generated filing name
  const filingName = (() => {
    const parts = [];
    if (selectedMatter) parts.push(selectedMatter);
    const typeLabel = OUTGOING_DOC_TYPES.find(dt => dt.value === selectedDocType)?.label;
    if (typeLabel) parts.push(typeLabel);
    if (toParty.trim()) parts.push(toParty.trim());
    if (dateSent) {
      try {
        const [y, m, d] = dateSent.split("-");
        parts.push(`${d}.${m}.${y}`);
      } catch {}
    }
    if (parts.length === 0) return "";
    const ext = record?.filename?.match(/\.[^.]+$/)?.[0] || ".pdf";
    return parts.join(" - ") + ext;
  })();

  // Matter search
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [searchingMatters, setSearchingMatters] = useState(false);
  // BM-H285: an unreadable matter list gets its own state, so the UI can say
  // "we could not look" rather than showing "No matters found".
  const [matterSearchError, setMatterSearchError] = useState("");
  const [showMatterResults, setShowMatterResults] = useState(false);

  const searchMatters = useCallback(async (query) => {
    if (!query || query.length < 2) { setMatterResults([]); return; }
    setSearchingMatters(true);
    try {
      // BM-H285: searchMatters throws rather than resolving empty when the matter
      // list could not be read, so a broken search can no longer masquerade as a
      // matter that does not exist.
      const results = await searchMattersApi(query);
      setMatterSearchError("");
      setMatterResults(results);
      setShowMatterResults(true);
    } catch (err) {
      setMatterResults([]);
      setMatterSearchError(
        err instanceof MatterSearchUnavailable
          ? err.message
          : MATTER_SEARCH_UNAVAILABLE_MESSAGE
      );
      setShowMatterResults(false);
    } finally {
      setSearchingMatters(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchMatters(matterSearch), 300);
    return () => clearTimeout(timer);
  }, [matterSearch, searchMatters]);

  const userEmail = "admin@bonsai.law";

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/postintake/outgoing/${itemId}`);
      const data = response.data;
      setRecord(data);
      // Pre-fill form from existing record data
      if (data.matter_ref) setSelectedMatter(data.matter_ref);
      if (data.document_type) setSelectedDocType(data.document_type);
      if (data.to_party) setToParty(data.to_party);
      if (data.notes) setNotes(data.notes);
      if (data.date_sent) setDateSent(data.date_sent.slice(0, 10));
    } catch (error) {
      console.error("Failed to fetch outgoing record:", error);
      toast.error("Failed to load document");
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const handleFile = async () => {
    if (!selectedMatter) {
      toast.error("File Reference is required — please assign a matter");
      return;
    }
    if (!toParty.trim()) {
      toast.error("To Party is required — please specify who this was sent to");
      return;
    }
    if (!selectedDocType) {
      toast.error("Please select a document type");
      return;
    }
    if (timeFieldProblem(timeBox)) {
      toast.error(timeFieldProblem(timeBox));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/postintake/outgoing/${itemId}/file`, {
        matter_ref: selectedMatter,
        document_type: selectedDocType,
        filing_name: filingName || null,
        to_party: toParty.trim() || null,
        date_sent: dateSent,
        user_email: userEmail,
        notes: notes,
      });

      toast.success("Outgoing document filed successfully");
      // BM-H4112: time for this letter; the document is filed either way.
      const t = await recordTouchpointTime({
        value: timeBox, matterRef: selectedMatter, source: "post", sourceRef: itemId,
      });
      if (t.recorded) toast.success("Time recorded");
      else if (t.error) toast.error(`Document filed, but time not recorded: ${t.error}`);
      navigate("/outgoing-post");
    } catch (error) {
      console.error("Filing failed:", error);
      toast.error(errorMessage(error, "Failed to file document"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) { toast.error("Please provide a reason"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/postintake/outgoing/${itemId}/reject`, {
        reason: rejectReason,
        user_email: userEmail,
      });
      toast.success("Document rejected");
      navigate("/outgoing-post");
    } catch {
      toast.error("Failed to reject document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailLink = async () => {
    setSendingLink(true);
    try {
      const userId =
        sessionStorage.getItem("bonsai_user_id") ||
        localStorage.getItem("bonsai_user_id") ||
        userEmail;
      const res = await axios.post(
        `${API}/postintake/outgoing/${itemId}/email-link`,
        {},
        { headers: { "X-Bonsai-User-Id": userId || "" } },
      );
      toast.success(`Link sent to ${res.data.to || "your email"}`);
    } catch (err) {
      toast.error(`Failed to send link — ${errorMessage(err, "Unknown error")}`);
    } finally {
      setSendingLink(false);
    }
  };

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMM yyyy");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
          <p className="mt-4 text-slate-500">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <p className="text-slate-500">Document not found</p>
      </div>
    );
  }

  const isPending = record.status === "pending";

  return (
    <>
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/outgoing-post")} className="gap-1">
                <ChevronLeft className="w-4 h-4" />
                Back to Queue
              </Button>
              <div>
                <h1 className="text-lg font-bold text-white" data-testid="outgoing-detail-filename">{record.filename}</h1>
                <p className="text-sm text-slate-500">
                  Scanned: {formatDate(record.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Document Preview */}
          <div className="bg-white rounded-md border border-navy-border p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-white">Document Preview</h2>
              {record.status === "filed" && (
                <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
                  <CheckCircle2 className="w-3 h-3" />Filed
                </Badge>
              )}
              {record.status === "pending" && (
                <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
                  <AlertCircle className="w-3 h-3" />Needs Assignment
                </Badge>
              )}
            </div>
            <DocumentPreview itemId={itemId} filename={record.filename} sharepointId={record.sharepoint_id} />
          </div>

          {/* Right: Filing Form */}
          <div className="space-y-6">

            {/* Filing Form (pending only) */}
            {isPending && (
              <div className="bg-white rounded-md border border-navy-border p-4 space-y-4" data-testid="filing-form">
                <h3 className="font-semibold text-white">Filing Details</h3>

                {/* Matter */}
                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    File Reference <span className="text-status-red">*</span>
                    {!selectedMatter && (
                      <span className="ml-2 text-xs font-bold text-status-red bg-status-red-dim px-2 py-0.5 rounded" data-testid="matter-required">MUST BE COMPLETED</span>
                    )}
                  </label>
                  {selectedMatter ? (
                    <div className="flex items-center gap-2 p-2 bg-teal-dim rounded-md border border-teal">
                      <span className="text-sm font-medium text-teal flex-1">{selectedMatter}</span>
                      <button
                        onClick={() => { setSelectedMatter(""); setMatterSearch(""); }}
                        className="text-slate-400 hover:text-status-red"
                        data-testid="clear-matter-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={matterSearch}
                        onChange={(e) => setMatterSearch(e.target.value)}
                        onFocus={() => matterResults.length > 0 && setShowMatterResults(true)}
                        placeholder="Search SharePoint folders..."
                        className="pl-10"
                        data-testid="matter-search-input"
                      />
                      {searchingMatters && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                      )}
                      <MatterSearchUnavailableNotice message={matterSearchError} />
                      {showMatterResults && matterResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="matter-results">
                          {matterResults.map((m) => (
                            <button
                              key={m.id}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-navy-hover/60 border-b border-navy-border last:border-0"
                              onClick={() => { setSelectedMatter(m.name); setShowMatterResults(false); setMatterSearch(""); }}
                            >
                              <span className="font-medium text-white">{m.name}</span>
                              {m.childCount > 0 && <span className="text-xs text-slate-400 ml-2">{m.childCount} items</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {showMatterResults && matterSearch.length >= 2 && matterResults.length === 0 && !searchingMatters && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg p-3">
                          <p className="text-sm text-slate-500">No folders found matching "{matterSearch}"</p>
                          <button
                            className="text-sm text-teal hover:underline mt-1"
                            onClick={() => { setSelectedMatter(matterSearch); setShowMatterResults(false); }}
                          >
                            Use "{matterSearch}" as reference
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-txt mb-1">Document Type *</label>
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger data-testid="doctype-select">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {OUTGOING_DOC_TYPES.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* To Party */}
                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    To Party <span className="text-status-red">*</span>
                    {!toParty.trim() && (
                      <span className="ml-2 text-xs font-bold text-status-red bg-status-red-dim px-2 py-0.5 rounded" data-testid="to-party-required">MUST BE COMPLETED</span>
                    )}
                  </label>
                  <Input
                    value={toParty}
                    onChange={(e) => setToParty(e.target.value)}
                    placeholder="e.g. Land Registry / Smith & Jones Solicitors"
                    className={`${!toParty.trim() ? "border-red-300 bg-status-red-dim/30" : "border-teal bg-teal-dim/30"}`}
                    data-testid="to-party-input"
                  />
                </div>

                {/* Date Sent */}
                <div>
                  <label className="block text-sm font-medium text-txt mb-1">Date Sent</label>
                  <Input
                    type="date"
                    value={dateSent}
                    onChange={(e) => setDateSent(e.target.value)}
                    data-testid="date-sent-input"
                  />
                </div>

                {/* Filing Name Preview */}
                {filingName && (
                  <div className="bg-navy-hover/60 rounded-md p-3 border border-navy-border" data-testid="filing-name-preview">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Filing Name</label>
                    <p className="text-sm font-medium text-white break-all">{filingName}</p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-txt mb-1">Notes</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>
              </div>
            )}

            {/* BM-H4112: optional time for this letter */}
            {isPending && (
              <RecordTimeField
                value={timeBox}
                onChange={setTimeBox}
                defaultNarrative={toParty.trim() ? `Letter to ${toParty.trim()}` : "Letter"}
                testIdPrefix="outgoing-post-time"
                light
              />
            )}

            {/* Actions */}
            {isPending && (
              <div className="flex flex-col gap-3" data-testid="action-buttons">
                <Button
                  onClick={handleFile}
                  disabled={submitting || !selectedMatter || !selectedDocType || !toParty.trim()}
                  className="w-full bg-teal hover:bg-teal/90 gap-2"
                  data-testid="file-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Claim & File
                </Button>

                <div className="flex gap-2">
                  <Input
                    placeholder="Rejection reason..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="flex-1"
                    data-testid="reject-reason-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    disabled={submitting || !rejectReason}
                    className="text-status-red border-red-200 hover:bg-status-red-dim"
                    data-testid="reject-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Filed Status */}
            {record.status === "filed" && (
              <div className="bg-teal-dim rounded-md border border-teal p-4" data-testid="filed-status">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal" />
                  <span className="font-medium text-teal">Filed Successfully</span>
                </div>
                <p className="text-sm text-teal mt-1">Filed to: {record.filed_to_path || record.matter_ref || "Unknown"}</p>
                {record.filed_by && <p className="text-sm text-teal">By: {record.filed_by} on {formatDate(record.filed_at)}</p>}
                {record.to_party && <p className="text-sm text-teal">To: {record.to_party}</p>}
                {record.filed_sharepoint_url && (
                  <a href={record.filed_sharepoint_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-sm text-teal hover:underline">
                    <ExternalLink className="w-4 h-4" /> Open in SharePoint
                  </a>
                )}
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEmailLink}
                    disabled={sendingLink}
                    className="gap-2 border-teal text-teal hover:bg-teal/10"
                    data-testid="email-saved-link-btn"
                  >
                    {sendingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Send me a link to the saved document
                  </Button>
                </div>
              </div>
            )}

            {/* Rejected Status */}
            {record.status === "rejected" && (
              <div className="bg-status-red-dim rounded-md border border-red-200 p-4" data-testid="rejected-status">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-status-red" />
                  <span className="font-medium text-status-red">Rejected</span>
                </div>
                <p className="text-sm text-status-red mt-1">Reason: {record.rejection_reason}</p>
                {record.rejected_by && <p className="text-sm text-status-red">By: {record.rejected_by} on {formatDate(record.rejected_at)}</p>}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
    {selectedMatter && (
      <BonsaiMate context="matter" matterReference={selectedMatter} />
    )}
    </>
  );
}
